import { SaleEventStatus, UserSubscriptionTier } from '../../../../../__gqltypes__';

/**
 * A bundle purchase reaches the backend as a store SKU only, with no bundle id. The backend
 * (`getSaleBundlePackFromSku`, Sp3_be concerns/currency/utils.ts) looks through the sale events
 * that are running and that the player participates in, and takes the first bundle on that offer:
 * when a player can see two bundles on the same offer, they may receive the wrong one. These
 * helpers find such collisions so the editor can refuse them - inside the edited event, and
 * against every other active event whose dates overlap it and whose matchers can let the same
 * player in.
 */

type BundleSku = { skuId?: string | null };
type BundleSale = { id: string; sku?: string | null; name?: string | null };
type DateValue = Date | string | null | undefined;

/** Matchers that can keep two audiences apart (the backend's `isParticipating`). */
export type SaleEventAudience = {
  filterUserIds?: ReadonlyArray<string> | null;
  filterExcludedUserIds?: ReadonlyArray<string> | null;
  filterSubscriptionTier?: UserSubscriptionTier | string | null;
  filterUserMinCreationDate?: DateValue;
  filterUserMaxCreationDate?: DateValue;
  filterSpendTiers?: ReadonlyArray<string> | null;
};

export type SaleEventForSkuCheck = SaleEventAudience & {
  id: string;
  name: string;
  status: SaleEventStatus | string;
  startDate?: DateValue;
  stopDate?: DateValue;
  packs?: ReadonlyArray<BundleSku> | null;
};

export type SkuConflict = {
  /** Offer id (`skuId`) the backend matches the purchase on. */
  skuId: string;
  /** Label of the offer as shown in the "Store offer (SKU)" dropdown. */
  label: string;
  /** Number of bundles of the edited event on this offer. */
  bundlesInEvent: number;
  /** Other events that one player can see at the same time as the edited one, on this offer. */
  otherEvents: Array<{ id: string; name: string }>;
};

type CheckInput = {
  saleEventId: string;
  startDate: DateValue;
  stopDate: DateValue;
  audience: SaleEventAudience;
  bundles: ReadonlyArray<BundleSku>;
  otherSaleEvents: ReadonlyArray<SaleEventForSkuCheck>;
  bundleSales: ReadonlyArray<BundleSale>;
  now?: number;
};

const toTime = (date: DateValue, fallback: number): number => {
  if (!date) {
    return fallback;
  }
  const time = new Date(date).getTime();
  return Number.isNaN(time) ? fallback : time;
};

// A missing date leaves that side open, so an undated event overlaps everything. Back-to-back
// events (one stops exactly when the next starts) do not overlap: scheduling the next sale on the
// same offers is the normal case.
export const datesOverlap = (aStart: DateValue, aStop: DateValue, bStart: DateValue, bStop: DateValue): boolean =>
  toTime(aStart, -Infinity) < toTime(bStop, Infinity) && toTime(bStart, -Infinity) < toTime(aStop, Infinity);

// An event that has already ended can no longer sell anything, so it never conflicts.
const hasEnded = (stopDate: DateValue, now: number): boolean => toTime(stopDate, Infinity) < now;

const isSet = (list: ReadonlyArray<string> | null | undefined): list is ReadonlyArray<string> =>
  !!list && list.length > 0;

// The backend turns PREMIUM/VIP into PLUS (or PLUS into VIP) depending on the client version, so
// those three can reach the same player; BASIC and AD_FREE only match themselves.
const PAID_TIERS: string[] = [UserSubscriptionTier.PREMIUM, UserSubscriptionTier.VIP, UserSubscriptionTier.PLUS];
const tierGroup = (tier: string): string => (PAID_TIERS.includes(tier) ? 'PAID' : tier);

/**
 * Whether one player can match both audiences. Only answers "no" when a matcher proves it - user
 * id lists, account creation ranges, spend tiers or subscription tiers that cannot meet. Level,
 * app version and "no subscription before" are minimums or one-sided, so they never separate two
 * audiences and are left out.
 */
export function audiencesCanOverlap(a: SaleEventAudience, b: SaleEventAudience): boolean {
  if (isSet(a.filterUserIds) || isSet(b.filterUserIds)) {
    const excluded = new Set([...(a.filterExcludedUserIds ?? []), ...(b.filterExcludedUserIds ?? [])]);
    const listed = isSet(a.filterUserIds) ? a.filterUserIds : b.filterUserIds ?? [];
    const playerInBoth = listed.some(
      (userId) =>
        !excluded.has(userId) &&
        (!isSet(a.filterUserIds) || a.filterUserIds.includes(userId)) &&
        (!isSet(b.filterUserIds) || b.filterUserIds.includes(userId))
    );
    if (!playerInBoth) {
      return false;
    }
  }

  if (
    a.filterSubscriptionTier &&
    b.filterSubscriptionTier &&
    tierGroup(a.filterSubscriptionTier) !== tierGroup(b.filterSubscriptionTier)
  ) {
    return false;
  }

  if (isSet(a.filterSpendTiers) && isSet(b.filterSpendTiers)) {
    const bTiers = b.filterSpendTiers;
    if (!a.filterSpendTiers.some((tier) => bTiers.includes(tier))) {
      return false;
    }
  }

  // Account creation date: the backend keeps users created within [min, max], both inclusive.
  const latestMin = Math.max(
    toTime(a.filterUserMinCreationDate, -Infinity),
    toTime(b.filterUserMinCreationDate, -Infinity)
  );
  const earliestMax = Math.min(
    toTime(a.filterUserMaxCreationDate, Infinity),
    toTime(b.filterUserMaxCreationDate, Infinity)
  );
  return latestMin <= earliestMax;
}

/** Other events one player can see at the same time as the edited one. */
const eventsVisibleToSamePlayers = (input: CheckInput, now: number) =>
  input.otherSaleEvents.filter(
    (event) =>
      event.id !== input.saleEventId &&
      event.status === SaleEventStatus.ACTIVE &&
      !hasEnded(event.stopDate, now) &&
      datesOverlap(input.startDate, input.stopDate, event.startDate, event.stopDate) &&
      audiencesCanOverlap(input.audience, event)
  );

const labelOf = (skuId: string, bundleSales: ReadonlyArray<BundleSale>): string => {
  const offer = bundleSales.find((o) => o.id === skuId);
  return offer?.name ? `${offer.name} (${offer.sku || skuId})` : skuId;
};

export function findSkuConflicts({ now = Date.now(), ...input }: CheckInput): SkuConflict[] {
  // Only the event being saved is checked, and only while it can still sell.
  if (hasEnded(input.stopDate, now)) {
    return [];
  }
  const countBySku = new Map<string, number>();
  input.bundles.forEach((bundle) => {
    if (bundle.skuId) {
      countBySku.set(bundle.skuId, (countBySku.get(bundle.skuId) ?? 0) + 1);
    }
  });

  const visibleEvents = eventsVisibleToSamePlayers(input, now);

  return Array.from(countBySku.entries())
    .map(([skuId, bundlesInEvent]) => ({
      skuId,
      label: labelOf(skuId, input.bundleSales),
      bundlesInEvent,
      otherEvents: visibleEvents
        .filter((event) => (event.packs ?? []).some((pack) => pack.skuId === skuId))
        .map(({ id, name }) => ({ id, name })),
    }))
    .filter((conflict) => conflict.bundlesInEvent > 1 || conflict.otherEvents.length > 0);
}

export const describeSkuConflict = (conflict: SkuConflict): string => {
  const parts = [];
  if (conflict.bundlesInEvent > 1) {
    parts.push(`used by ${conflict.bundlesInEvent} bundles of this sale event`);
  }
  if (conflict.otherEvents.length > 0) {
    const names = conflict.otherEvents.map((event) => `"${event.name}"`).join(', ');
    parts.push(`also used by ${names}, which the same players can see at the same time`);
  }
  return `${conflict.label} is ${parts.join(' and ')}`;
};

/**
 * First offer not used by the edited event nor by an event the same players can see at the same
 * time, for new bundles. Falls back to the first offer when every one is taken (the conflict check
 * then reports it).
 */
export function pickFreeBundleOffer({ now = Date.now(), ...input }: CheckInput): string | undefined {
  const taken = new Set<string>();
  const addTaken = (packs: ReadonlyArray<BundleSku> | null | undefined) =>
    (packs ?? []).forEach((pack) => pack.skuId && taken.add(pack.skuId));
  addTaken(input.bundles);
  eventsVisibleToSamePlayers(input, now).forEach((event) => addTaken(event.packs));
  return (input.bundleSales.find((offer) => !taken.has(offer.id)) ?? input.bundleSales[0])?.id;
}
